/**
 *
 * FormSharedSubDetailPageElements
 *
 */
import {
  AsseticChip,
  FormTextControl,
  FormNumberControl,
  FormSearchableControl,
} from '@assetic/web-components';
import { Grid, Box, useTheme } from '@mui/material';
import { FastField, Field, useField } from 'formik';
import * as React from 'react';
import {
  ContentArrayPayload,
  searchableItems,
  SubAddOnTypesPayload,
  SubscriptionStatus,
} from 'types/models';
import { FormSubscriptionTypeSelect } from '../FormSubscriptionTypeSelect';
import { SectionHeader } from '../Headers';
import { DataRegionLabelMap } from './constant';
import fonts from 'app/assets/styles/fonts';
import { Link } from '../Link';
import { FormDateTimePicker } from '../FormDateTimePicker';
import { FormCheckBoxCard } from '../FormCheckBoxCard';
import { UrlToUser } from 'app/constants';
import { UsageCountChip } from '../UsageCountChip';
import { useSelector } from 'react-redux';
import {
  DEPRECATED_PLAN_TYPES,
  selectPreloadSubPlanSearchable,
} from 'app/helpers/PreloadStateHook/slice/selectors';
import { ChipColorMap } from '../SubscriptionCardSharedComponent';
import { FormSearchableTextField } from '../FormSearchableTextField';
import { API_ENDPOINTS } from 'utils/configs';
import { convertUserToEmailSearchable } from '../FormSearchableTextField/helpers';

interface SettingProps {
  status: SubscriptionStatus;
  disabledPlanType?: boolean;
  requireDataRegionWarning?: boolean;
}

export function FormSharedSubDetailSettings(props: SettingProps) {
  const {
    status,
    disabledPlanType = true,
    requireDataRegionWarning = true,
  } = props;
  const [, data_region_meta] = useField('data_region');
  const {
    palette: {
      warning: { main: warningMain },
    },
  } = useTheme();
  const subscriptionPlanTypeSearchable = useSelector(
    selectPreloadSubPlanSearchable,
  );
  const isDataRegionDirty =
    data_region_meta.initialValue !== data_region_meta.value;
  return (
    <>
      <SectionHeader title="Settings" />
      <Grid container spacing={2}>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box height={56}>
                <Box color="text.secondary" fontWeight={500}>
                  Subscription Status
                </Box>

                <AsseticChip
                  color={ChipColorMap(status)}
                  label={status}
                  uppercase
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <FastField
                name="subscription_id"
                label="Subscription ID"
                component={FormTextControl}
                disabled
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Field
                name="plan_type"
                label="Plan Type"
                component={FormSearchableControl}
                disabled={disabledPlanType}
                items={subscriptionPlanTypeSearchable}
                getOptionDisabled={option =>
                  DEPRECATED_PLAN_TYPES.includes(option?.value)
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FastField
                name="data_region"
                label="Data Region"
                component={FormSearchableControl}
                items={DataRegionLabelMap}
                required
                error={true}
                helperText={
                  requireDataRegionWarning && isDataRegionDirty ? (
                    <Box color={warningMain} fontSize={fonts.small}>
                      The updated data region will ONLY be applied to newly
                      created portfolios.
                    </Box>
                  ) : null
                }
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FastField
                name="subscription_type"
                component={FormSubscriptionTypeSelect}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

export const ownerGenerateHelperText = (
  searchableItems: searchableItems,
  value: string,
) => {
  if (value) {
    if (
      searchableItems.length === 0 ||
      !searchableItems.find(item => item.value === value)
    ) {
      return (
        <Box color={'warning.main'}>
          "{value}" is not found. New user will be created
        </Box>
      );
    }
  }

  return null;
};

export const oneYearFromNow = new Date();
oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
export const todayWithoutTime = new Date(
  `${new Date().toISOString().split('T')[0]}T00:00:00Z`,
);
export const expireDateGenerateHelperText = (date: Date) => {
  if (date) {
    if (date < todayWithoutTime) {
      return (
        <Box color={'warning.main'}>
          You have entered a past date :"
          {new Intl.DateTimeFormat('en-AU', {
            timeStyle: 'medium',
            dateStyle: 'medium',
          }).format(date)}
          ".
        </Box>
      );
    } else if (date <= oneYearFromNow && date > todayWithoutTime) {
      return (
        <Box color={'warning.main'}>
          You have entered an expiry period less than 1 year.
        </Box>
      );
    }
  }
  return null;
};

export const expireDateLargeThanTodayHelperText = (date: Date) => {
  if (date) {
    if (date < todayWithoutTime) {
      return (
        <Box color={'warning.main'}>
          You have entered a past date :"
          {new Intl.DateTimeFormat('en-AU', {
            timeStyle: 'medium',
            dateStyle: 'medium',
          }).format(date)}
          ".
        </Box>
      );
    }
  }
  return null;
};

interface OwnerProps {
  owner_id?: string;
  showViewUser?: boolean;
  disableOwnerField?: boolean;
}
export function FormSharedSubDetailOwner(props: OwnerProps) {
  const {
    owner_id = '',
    showViewUser = true,
    disableOwnerField = false,
  } = props;

  return (
    <>
      <SectionHeader title="Owner" />
      <Grid container spacing={2} alignItems="center">
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormSearchableTextField
                label="Owner"
                name={'owner.email'}
                path={API_ENDPOINTS.USERS}
                filterFields={['email']}
                required={true}
                convertToSearchable={convertUserToEmailSearchable}
                disabled={disableOwnerField}
                generateHelperText={ownerGenerateHelperText}
                paginationType={'pagiUser'}
              />
            </Grid>
          </Grid>
        </Grid>
        {owner_id && showViewUser && (
          <Grid item lg={4} xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Link to={UrlToUser(owner_id).detail}>View Detail</Link>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}

interface PortfolioProps {
  portfolio_usage: number;
  portfolio_limit: number;
  requireTaskLimit?: boolean;
  requirePfUsage?: boolean;
}
export function FormSharedSubDetailPortfolio(props: PortfolioProps) {
  const {
    portfolio_usage,
    portfolio_limit,
    requireTaskLimit = true,
    requirePfUsage = true,
  } = props;
  return (
    <>
      <SectionHeader title="Portfolio" />
      <Grid container spacing={2}>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FastField
                name="portfolio_limit"
                label="Portfolio Limit"
                required
                component={FormNumberControl}
                min={portfolio_usage}
              />
            </Grid>
          </Grid>
        </Grid>
        {requireTaskLimit && (
          <Grid item lg={4} xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FastField
                  name="task_history_limit"
                  label="Task History Limit"
                  required
                  component={FormNumberControl}
                />
              </Grid>
            </Grid>
          </Grid>
        )}
        {requirePfUsage && (
          <Grid item lg={4} xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box height={56}>
                  <Box color="text.secondary" fontWeight={500}>
                    Portfolio Usages
                  </Box>
                  <Box>
                    <Link to={'#'}>
                      <UsageCountChip
                        usage={portfolio_usage}
                        limit={portfolio_limit}
                        requireLimit={false}
                        overrideChipStyle={{
                          textDecoration: 'underline',
                        }}
                      />
                    </Link>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
}

interface BillingInfoProps {}

export function FormSharedBillingInfo(props: BillingInfoProps) {
  return (
    <>
      <SectionHeader title="Billing Info" />
      <Grid container spacing={2}>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FastField
                name="customer_email"
                label="Email"
                type="email"
                component={FormTextControl}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FastField
                name="customer_company"
                label="Company Name"
                component={FormTextControl}
                required
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FastField
                name="customer_firstname"
                label="First Name"
                component={FormTextControl}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FastField
                name="customer_lastname"
                label="Last Name"
                component={FormTextControl}
                required
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item lg={4} xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FastField
                name="expiry_date"
                label="Expiry Date"
                required
                component={FormDateTimePicker}
                generateHelperText={expireDateGenerateHelperText}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}

interface AddonsProps {
  subAddonErr: boolean;
  subAddons: ContentArrayPayload<SubAddOnTypesPayload> | null;
}
export function FormSharedAddons(props: AddonsProps) {
  const { subAddonErr, subAddons } = props;
  let content;
  if (subAddonErr || (!subAddonErr && !subAddons?.contents?.length)) {
    content = (
      <div>Could not get subscription addons. Please try again later.</div>
    );
  } else {
    content = (
      <Grid container spacing={2}>
        {subAddons!.contents.map(addon => {
          if (addon.sub_addon_id) {
            return (
              <Grid item lg={4} xs={12} key={addon.sub_addon_id}>
                <FastField
                  name="features"
                  type="checkbox"
                  value={addon.sub_addon_id}
                  component={FormCheckBoxCard}
                  label={addon.sub_addon_label}
                  helperText={addon.description}
                  size={'normal'}
                />
              </Grid>
            );
          }
          return null;
        })}
      </Grid>
    );
  }

  return (
    <>
      <SectionHeader title="Addons" />
      {content}
    </>
  );
}
